/**
 * @file ProductKindSection.tsx
 *
 * The Simple/Variable chooser — a merchant-facing choice with no DB flag: a variable
 * product is one with ≥1 variation axis, a simple product has none. Distinct from
 * {@link ProductTypeSection} (physical/digital); rendered only for a PHYSICAL product
 * (digital is always a single variant). Choosing Variable reveals the option-axis editor
 * and the per-variant table in place of the product-level pricing block; switching back to
 * Simple while variants exist is confirmed by the parent (it discards per-variant data).
 */

import { RichOptionItem } from '@group.one/react-gravity';
import { useIntl } from 'react-intl';
import { adminMessages } from '../../../i18n/messages';
import styles from '../ProductForm.module.css';

/** Whether the product is a single variant (simple) or has variation axes (variable). */
export type ProductKind = 'simple' | 'variant';

interface ProductKindSectionProps {
  value: ProductKind;
  onChange: (value: ProductKind) => void;
}

/**
 * Renders the simple/variable product chooser.
 *
 * @param props - The current kind and a change handler.
 *
 * @returns The product-kind section.
 */
export function ProductKindSection({ value, onChange }: ProductKindSectionProps) {
  const intl = useIntl();
  return (
    <div className={styles.typeOptions} data-testid="product-form-kind">
      <RichOptionItem
        value="simple"
        name="productKind"
        title={intl.formatMessage(adminMessages.formKindSimple)}
        description={intl.formatMessage(adminMessages.formKindSimpleDescription)}
        condensed
        checked={value === 'simple'}
        onChange={() => onChange('simple')}
      />
      <RichOptionItem
        value="variant"
        name="productKind"
        title={intl.formatMessage(adminMessages.formKindVariant)}
        description={intl.formatMessage(adminMessages.formKindVariantDescription)}
        condensed
        checked={value === 'variant'}
        onChange={() => onChange('variant')}
      />
    </div>
  );
}
